import React from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Sync as SyncIcon,
  CloudOff as OfflineIcon,
  CloudDone as OnlineIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Home as HomeIcon,
  Receipt as ReceiptIcon,
  Add as AddIcon,
  Payment as PaymentIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { AppState } from '@/types';
import { appStateService } from '@/services/appStateService';
import { syncService } from '@/services/syncService';
import { notificationService } from '@/services/notificationService';
import { apiService } from '@/services/api';

interface AppLayoutProps {
  children: React.ReactNode;
  title: string;
  showSyncButton?: boolean;
  onSync?: () => void;
  onNavigate?: (view: string) => void;
  onNavigateToCredits?: () => void;
  currentView?: string; // Aggiunto per identificare il contesto corrente
}

const DRAWER_WIDTH = 280;

export const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  title,
  showSyncButton = true,
  onSync,
  onNavigate,
  onNavigateToCredits,
  currentView,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [appState, setAppState] = React.useState<AppState>(appStateService.getState());
  const [isSyncing, setIsSyncing] = React.useState(false);

  React.useEffect(() => {
    const handleStateChange = (newState: AppState) => {
      setAppState(newState);
    };

    appStateService.addStateListener(handleStateChange);
    return () => appStateService.removeStateListener(handleStateChange);
  }, []);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      // Esegui logout completo con pulizia di tutti i dati
      await appStateService.performLogout();
      handleProfileMenuClose();
    } catch (error) {
      console.error('Errore durante il logout:', error);
      // Fallback: almeno resetta lo stato locale
      appStateService.setUnauthenticated();
      handleProfileMenuClose();
    }
  };

  const handleSync = async () => {
    if (isSyncing) return;
    
    console.log('🔄 Sync started from AppLayout, currentView:', currentView);
    setIsSyncing(true);
    
    try {
      if (currentView === 'new-donation') {
        // Contesto: Nuova Donazione - Carica donatori per ricerca offline
        console.log('📋 Loading donors for offline search...');
        notificationService.info('Caricamento', 'Caricamento elenco donatori...');
        
        try {
          const donors = await apiService.refreshDonorsCache();
          console.log(`✅ Loaded ${donors.length} donors for offline search`);
          notificationService.success('Completato', `Elenco donatori caricato (${donors.length} donatori)`);
        } catch (error) {
          console.error('❌ Failed to load donors:', error);
          notificationService.error('Errore', 'Impossibile caricare l\'elenco donatori');
        }
      } else {
        // Contesto: Dashboard - Sincronizzazione normale
        console.log('📊 Dashboard sync - updating statistics...');
        notificationService.syncStarted();
        
        const result = await syncService.forceSync();
        
        if (result.success) {
          console.log('✅ Sync completed successfully:', result);
          notificationService.syncCompleted(result.syncedDonations, result.syncedReceipts);
        } else {
          console.log('⚠️ Sync completed with errors:', result.errors);
          notificationService.syncFailed(result.errors);
        }
      }
      
      // Sempre emetti l'evento dopo qualsiasi sync completato
      console.log('📡 Dispatching dashboardDataUpdate event...');
      const event = new CustomEvent('dashboardDataUpdate', {
        detail: { 
          timestamp: Date.now(),
          currentView: currentView
        }
      });
      window.dispatchEvent(event);
      console.log('📡 Event dispatched:', event.detail);
    } catch (error) {
      console.error('❌ Sync failed:', error);
      notificationService.error('Errore', 'Operazione fallita');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      notificationService.info('Test Connessione', 'Verifica connessione in corso...');
      
      // Aggiorna lo stato della connessione usando il metodo pubblico
      await appStateService.refreshConnectionStatus();
      
      // Ottieni lo stato aggiornato
      const currentState = appStateService.getState();
      
      if (currentState.isOnline) {
        notificationService.success('Connessione OK', 'La connessione al server è attiva');
      } else {
        notificationService.error('Connessione Fallita', 'Impossibile raggiungere il server');
      }
    } catch (error) {
      notificationService.error('Errore Test', 'Errore durante il test di connessione');
    }
  };

  const handleMenuNavigation = (view: string) => {
    // Prima naviga alla nuova pagina
    onNavigate?.(view);
    // Aspetta che la transizione della pagina sia completata (300ms fade + 400ms slide)
    setTimeout(() => {
      setDrawerOpen(false);
    }, 500);
  };

  const menuItems = [
    { icon: <HomeIcon />, text: 'Dashboard', view: 'dashboard', action: () => handleMenuNavigation('dashboard') },
    { icon: <AddIcon />, text: 'Nuova Donazione', view: 'new-donation', action: () => handleMenuNavigation('new-donation') },
    { icon: <PaymentIcon />, text: 'Donazione elettronica', view: 'electronic-donation', action: () => handleMenuNavigation('electronic-donation') },
    { icon: <ReceiptIcon />, text: 'Le Mie Donazioni', view: 'donations-list', action: () => handleMenuNavigation('donations-list') },
  ];

  const drawer = (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
        background: 'background.default', // Usa il gradiente del tema
    }}>
      {/* Header Drawer - Modern Neutral */}
      <Box sx={{ 
        p: 3, 
        background: 'background.default', // Usa il gradiente del tema
        color: 'text.primary',
        position: 'relative',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      }}>
        <Typography variant="h5" noWrap sx={{ 
          fontWeight: 'bold', 
          mb: 0.5,
          color: 'text.primary',
        }}>
          {appState.user ? `${appState.user.firstName} ${appState.user.lastName}` : 'Field App'}
        </Typography>
        <Typography variant="body2" sx={{ 
          color: 'text.secondary',
          fontWeight: 500,
        }}>
          Fondazione Donazioni
        </Typography>
      </Box>

      {/* User Info - Modern Card */}
      {appState.user && (
        <Box sx={{ p: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            p: 2,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid rgba(25, 118, 210, 0.1)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
              transform: 'translateY(-1px)',
            }
          }}>
            <Avatar sx={{ 
              bgcolor: 'primary.main', 
              color: 'white',
              width: 48,
              height: 48,
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
            }}>
              <PersonIcon />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" noWrap sx={{ 
                fontWeight: 'bold',
                color: 'text.primary',
                mb: 0.5
              }}>
                {appState.user.firstName} {appState.user.lastName}
              </Typography>
              <Typography variant="caption" sx={{ 
                color: 'text.secondary',
                display: 'block'
              }}>
                Operatore Fondazione
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Menu Items - Modern Design */}
      <List sx={{ flex: 1, py: 2, px: 1 }}>
        {menuItems.map((item, index) => {
          const isActive = currentView === item.view;
          return (
            <ListItem
              key={index}
              button
              onClick={item.action}
              sx={{
                borderRadius: 2,
                mb: 1,
                mx: 1,
                py: 1.5,
                px: 2,
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                background: isActive 
                  ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.12) 0%, rgba(25, 118, 210, 0.08) 100%)'
                  : 'transparent',
                border: isActive 
                  ? '1px solid rgba(25, 118, 210, 0.2)'
                  : '1px solid transparent',
                boxShadow: isActive 
                  ? '0 2px 8px rgba(25, 118, 210, 0.15)'
                  : 'none',
                '&:hover': {
                  bgcolor: isActive 
                    ? 'rgba(25, 118, 210, 0.15)'
                    : 'rgba(25, 118, 210, 0.08)',
                  transform: 'translateX(4px)',
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.15)',
                },
                '&:active': {
                  transform: 'translateX(2px)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: '0',
                    height: '0',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(25, 118, 210, 0.3) 0%, rgba(25, 118, 210, 0.1) 50%, transparent 100%)',
                    transform: 'translate(-50%, -50%)',
                    animation: 'rippleEffect 0.6s ease-out',
                    zIndex: 1,
                  }
                },
                '@keyframes rippleEffect': {
                  '0%': {
                    width: '0',
                    height: '0',
                    opacity: 1,
                  },
                  '100%': {
                    width: '200px',
                    height: '200px',
                    opacity: 0,
                  },
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.02) 100%)',
                  opacity: 0,
                  transition: 'opacity 0.2s ease',
                  pointerEvents: 'none',
                },
                '&:active::after': {
                  opacity: 1,
                }
              }}
            >
              <ListItemIcon sx={{ 
                minWidth: 40,
                color: isActive ? 'primary.main' : 'primary.main',
                transition: 'color 0.3s ease',
                position: 'relative',
                zIndex: 2,
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{
                  fontWeight: isActive ? 600 : 500,
                  fontSize: '0.95rem',
                  color: isActive ? 'primary.main' : 'text.primary',
                }}
                sx={{ position: 'relative', zIndex: 2 }}
              />
            </ListItem>
          );
        })}
      </List>

      {/* Credits Footer - Modern Design */}
      <Box sx={{ p: 2, mt: 'auto' }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5, 
            mb: 2,
            cursor: 'pointer',
            p: 1.5,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
            border: '1px solid rgba(25, 118, 210, 0.1)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.05)',
              borderColor: 'rgba(25, 118, 210, 0.2)',
              transform: 'translateY(-1px)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }
          }}
          onClick={() => {
            onNavigateToCredits?.() || onNavigate?.('credit');
            setTimeout(() => {
              setDrawerOpen(false);
            }, 500);
          }}
        >
          <InfoIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
          <Typography variant="body2" sx={{ 
            color: 'primary.main',
            fontWeight: 500,
          }}>
            Informazioni App
          </Typography>
        </Box>
        
        {appState.pendingSyncCount > 0 && (
          <Box sx={{
            p: 1.5,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
            border: '1px solid rgba(255, 152, 0, 0.2)',
          }}>
            <Typography variant="caption" sx={{ 
              color: 'warning.dark',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}>
              <SyncIcon sx={{ fontSize: '1rem' }} />
              {appState.pendingSyncCount} elementi in attesa
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          bgcolor: 'white',
          color: 'text.primary',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>

          {/* Sync Button */}
          {showSyncButton && appState.isOnline && (
            <IconButton
              color="inherit"
              onClick={onSync || handleSync}
              disabled={isSyncing}
              title={
                isSyncing 
                  ? (currentView === 'new-donation' 
                      ? "Caricamento elenco donatori..." 
                      : "Caricamento dati dashboard...")
                  : currentView === 'new-donation' 
                    ? "Carica elenco donatori per ricerca offline" 
                    : "Sincronizza dati e aggiorna dashboard"
              }
              sx={{ 
                mr: 1,
                animation: isSyncing ? 'pulseAndSpin 1.5s ease-in-out infinite' : 'none',
                transform: isSyncing ? 'scale(1.1)' : 'scale(1)',
                transition: 'all 0.3s ease',
                backgroundColor: isSyncing ? 'primary.50' : 'transparent',
                '&:hover': {
                  backgroundColor: isSyncing ? 'primary.100' : 'action.hover',
                  transform: isSyncing ? 'scale(1.15)' : 'scale(1.05)',
                },
                '@keyframes pulseAndSpin': {
                  '0%': { 
                    transform: 'rotate(0deg) scale(1)',
                    backgroundColor: 'primary.50'
                  },
                  '25%': { 
                    transform: 'rotate(90deg) scale(1.1)',
                    backgroundColor: 'primary.100'
                  },
                  '50%': { 
                    transform: 'rotate(180deg) scale(1.2)',
                    backgroundColor: 'primary.200'
                  },
                  '75%': { 
                    transform: 'rotate(270deg) scale(1.1)',
                    backgroundColor: 'primary.100'
                  },
                  '100%': { 
                    transform: 'rotate(360deg) scale(1)',
                    backgroundColor: 'primary.50'
                  },
                },
              }}
            >
              <Badge 
                badgeContent={appState.pendingSyncCount} 
                color={appState.pendingSyncCount > 0 ? "warning" : "default"}
                sx={{
                  '& .MuiBadge-badge': {
                    animation: isSyncing ? 'pulse 1s ease-in-out infinite' : 'none',
                    '@keyframes pulse': {
                      '0%': { transform: 'scale(1)' },
                      '50%': { transform: 'scale(1.2)' },
                      '100%': { transform: 'scale(1)' },
                    },
                  },
                }}
              >
                <SyncIcon 
                  sx={{ 
                    fontSize: '1.5rem',
                    color: isSyncing ? 'primary.main' : 'inherit',
                    filter: isSyncing ? 'drop-shadow(0 0 4px rgba(25, 118, 210, 0.3))' : 'none',
                  }} 
                />
              </Badge>
            </IconButton>
          )}

          {/* Status Indicator */}
          <IconButton 
            color="inherit" 
            sx={{ mr: 1 }}
            onClick={handleTestConnection}
            title={appState.isOnline ? "Clicca per testare la connessione" : "Clicca per testare la connessione"}
          >
            {appState.isOnline ? <OnlineIcon color="success" /> : <OfflineIcon color="warning" />}
          </IconButton>

          {/* Profile Menu */}
          {appState.user && (
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls="primary-search-account-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                {appState.user?.firstName?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant={isMobile ? 'temporary' : 'persistent'}
          open={drawerOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          transitionDuration={300}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              marginTop: '-30px',
              border: 'none',
              boxShadow: '2px 0 20px rgba(0,0,0,0.08)',
              background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
              transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
            },
            '& .MuiBackdrop-root': {
              transition: 'opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: '100vh',
          bgcolor: 'grey.50',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Toolbar /> {/* Spacer for AppBar */}
        <Box sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: 2,
          minHeight: 0,
          background: 'background.default', // Usa il gradiente del tema
        }}>
          {children}
        </Box>
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
};
